import { Link } from 'react-router-dom';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

const services = [
  {
    icon: "ri-phone-fill",
    title: "AI Lead Capture Systems",
    desc: "AI voice agents, SMS text-back, and web chat—every lead captured the moment they reach out.",
    tag: "24/7 Coverage",
    image: "https://public.readdy.ai/ai/img_res/a241bb7fb8d6c0eb88c862a5d7d32bbd.jpg",
  },
  {
    icon: "ri-calendar-check-line",
    title: "AI Booking & Scheduling",
    desc: "Smart calendar automation with appointment qualification and intelligent routing logic built in.",
    tag: "Revenue First",
    image: "https://readdy.ai/api/search-image?query=abstract%20minimal%20digital%20calendar%20scheduling%20system%20geometric%20grid%20dots%20sapphire%20blue%20accent%20glow%20dark%20charcoal%20background%20enterprise%20technology%20modern%20clean%20appointment%20automation%20cinematic&width=800&height=520&seq=svc-tile-booking-1&orientation=landscape",
  },
  {
    icon: "ri-mail-send-line",
    title: "AI Follow-Up & Nurture",
    desc: "Automated SMS and email sequences that nurture leads and reactivate cold pipelines.",
    tag: "Zero Drop-Off",
    image: "https://readdy.ai/api/search-image?query=abstract%20flowing%20message%20stream%20email%20sequence%20automation%20pipeline%20glowing%20dots%20dark%20background%20deep%20navy%20blue%20minimal%20clean%20enterprise%20digital%20technology%20cinematic%20wide&width=800&height=400&seq=svc-tile-followup-1&orientation=landscape",
  },
  {
    icon: "ri-flow-chart",
    title: "CRM & Workflow Systems",
    desc: "Pipeline automation and internal workflows that run your business without manual effort.",
    tag: "Fully Automated",
    image: "https://readdy.ai/api/search-image?query=abstract%20crm%20workflow%20pipeline%20automation%20diagram%20minimal%20dark%20charcoal%20background%20geometric%20blue%20accent%20glowing%20connection%20nodes%20architecture%20enterprise%20technology%20modern%20clean&width=800&height=400&seq=svc-tile-crm-1&orientation=landscape",
  },
  {
    icon: "ri-line-chart-line",
    title: "Revenue Optimization",
    desc: "Conversion tracking and funnel performance analysis to increase revenue without adding headcount.",
    tag: "Performance Driven",
    image: "https://readdy.ai/api/search-image?query=abstract%20revenue%20performance%20analytics%20growth%20visualization%20sapphire%20blue%20gradient%20lines%20chart%20data%20metrics%20dark%20background%20minimal%20enterprise%20digital%20technology%20modern%20cinematic%20wide&width=800&height=400&seq=svc-tile-revenue-1&orientation=landscape",
  },
];

const ServicesSectionHome = () => {
  const { ref: headRef, visible: headVisible } = useScrollReveal();
  const { ref: topRef, visible: topVisible } = useScrollReveal();
  const { ref: botRef, visible: botVisible } = useScrollReveal();

  return (
    <section className="bg-[#111] py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={headRef}
          className={`reveal-el ${headVisible ? 'is-visible' : ''} flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14`}
        >
          <div>
            <p className="text-sapphire text-xs font-bold uppercase tracking-widest mb-5">What We Build</p>
            <h2 className="text-5xl lg:text-6xl xl:text-7xl font-black text-white leading-[1.04] tracking-tight">
              Our AI Systems
            </h2>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-semibold transition-all cursor-pointer whitespace-nowrap group"
          >
            View All Services
            <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>

        {/* Top 2 large tiles */}
        <div ref={topRef} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {services.slice(0, 2).map((s, i) => (
            <div
              key={s.title}
              className={`reveal-scale stagger-${i + 1} ${topVisible ? 'is-visible' : ''} relative rounded-3xl overflow-hidden h-80 group cursor-default`}
            >
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
              <div className="absolute top-6 right-6">
                <span className="text-xs font-semibold text-white/70 bg-white/10 border border-white/10 px-3 py-1.5 rounded-full whitespace-nowrap">
                  {s.tag}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/15 mb-4">
                  <i className={`${s.icon} text-white text-lg`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{s.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom 3 smaller tiles */}
        <div ref={botRef} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {services.slice(2).map((s, i) => (
            <div
              key={s.title}
              className={`reveal-scale stagger-${i + 1} ${botVisible ? 'is-visible' : ''} relative rounded-3xl overflow-hidden h-60 group cursor-default`}
            >
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"></div>
              <div className="absolute top-5 right-5">
                <span className="text-xs font-semibold text-white/70 bg-white/10 border border-white/10 px-3 py-1 rounded-full whitespace-nowrap">
                  {s.tag}
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/15 mb-3">
                  <i className={`${s.icon} text-white text-base`}></i>
                </div>
                <h3 className="text-base font-bold text-white mb-1">{s.title}</h3>
                <p className="text-white/50 text-xs leading-relaxed line-clamp-2">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSectionHome;
